/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Image,
  StatusBar,
  ActivityIndicator,
  RefreshControl,
  Animated,
  Alert,
  ScrollView,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

// ==================== ✅ API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  notifications: `${API_BASE_URL}/api/erp/notifications`, // ERP notifications endpoint
};

// ✅ Axios instance with API key and auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default function NotificationsProjectScreen() {
  const navigation = useNavigation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterType, setFilterType] = useState('All');
  const [companyLogo, setCompanyLogo] = useState(null);

  // Animation value for new notification indicator
  const pulseAnim = new Animated.Value(1);

  // Filter options
  const filterOptions = ['All', 'Projects', 'Tasks', 'Alerts', 'Updates'];

  // Load company logo on mount
  useEffect(() => {
    loadCompanyLogo();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }

      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  // Start pulse animation for new notifications
  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.2,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, []);

  // Fetch notifications from API
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Transform API notification to internal format
  const transformNotification = (apiNotif) => {
    // Map API fields to the structure expected by the UI
    // Example: type might be a string like "project", "task", "alert", "update"
    // We'll convert to capitalized form (Projects, Tasks, Alerts, Updates)
    let type = apiNotif.type || 'Update';
    type = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    // Ensure it's one of our filter options, else default to 'Updates'
    if (!['Projects', 'Tasks', 'Alerts', 'Updates'].includes(type)) {
      type = 'Updates';
    }

    // Priority mapping
    const priorityMap = {
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    };
    const priority = priorityMap[apiNotif.priority?.toLowerCase()] || 'Medium';

    // Related project info (if any)
    const project = apiNotif.related_project
      ? { id: apiNotif.related_project.id, title: apiNotif.related_project.name }
      : { id: 'unknown', title: 'General' };

    return {
      id: apiNotif.id.toString(),
      type: type,
      title: apiNotif.title || 'Notification',
      message: apiNotif.message || apiNotif.content || '',
      project: project,
      timestamp: apiNotif.created_at ? new Date(apiNotif.created_at) : new Date(),
      read: apiNotif.read || false,
      priority: priority,
      actionRequired: apiNotif.action_required || false,
      actionText: apiNotif.action_text || '',
    };
  };

  // Fallback mock data
  const mockNotifications = [
    // ... (same as before – keep for fallback)
  ];

  // Fetch notifications from API
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await api.get(API_ENDPOINTS.notifications, {
        params: { limit: 50, unread_only: false },
      });
      if (response.data?.success && Array.isArray(response.data.data)) {
        const transformed = response.data.data.map(transformNotification);
        setNotifications(transformed);
      } else {
        // Fallback to mock
        setNotifications(mockNotifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Fallback to mock
      setNotifications(mockNotifications);
    } finally {
      setLoading(false);
    }
  };

  // Handle refresh
  const onRefresh = async () => {
    setRefreshing(true);
    await fetchNotifications();
    setRefreshing(false);
  };

  // Handle back button navigation
  const handleGoBack = () => {
    navigation.goBack();
  };

  // Mark notification as read (local update – could also call API)
  const markAsRead = (id) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
    // Optionally call API to mark as read: api.patch(`${API_ENDPOINTS.notifications}/${id}`, { read: true })
  };

  // Handle notification press
  const handleNotificationPress = (notification) => {
    markAsRead(notification.id);
    // Navigate based on type (you can extend this)
    if (notification.type === 'Projects') {
      navigation.navigate('ProjectDetails', { projectId: notification.project.id });
    } else if (notification.type === 'Tasks') {
      Alert.alert('View Task', `Task details for ${notification.project.title}`);
    } else if (notification.type === 'Alerts' && notification.actionRequired) {
      if (notification.actionText === 'Review Budget') {
        navigation.navigate('BudgetSettingsProjectScreen', { projectId: notification.project.id });
      } else {
        Alert.alert('Action Required', notification.actionText);
      }
    } else {
      // Default action
      Alert.alert('Notification', notification.message);
    }
  };

  // Handle action button press
  const handleActionPress = (notification) => {
    markAsRead(notification.id);
    handleNotificationPress(notification); // reuse same logic
  };

  // Mark all as read (local)
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    Alert.alert('Success', 'All notifications marked as read');
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diffMs = now - timestamp;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };

  // Get notification icon
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'Projects':
        return <Ionicons name="business" size={24} color="#0e4e96" />;
      case 'Tasks':
        return <Ionicons name="checkmark-circle" size={24} color="#10B981" />;
      case 'Alerts':
        return <Ionicons name="warning" size={24} color="#F59E0B" />;
      case 'Updates':
        return <Ionicons name="refresh-circle" size={24} color="#8B5CF6" />;
      default:
        return <Ionicons name="notifications" size={24} color="#64748B" />;
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return '#EF4444';
      case 'Medium':
        return '#F59E0B';
      case 'Low':
        return '#10B981';
      default:
        return '#64748B';
    }
  };

  // Get filtered notifications
  const getFilteredNotifications = () => {
    if (filterType === 'All') {
      return notifications;
    }
    return notifications.filter(n => n.type === filterType);
  };

  // Count unread notifications
  const unreadCount = notifications.filter(n => !n.read).length;

  // Render notification item
  const renderNotificationItem = ({ item }) => (
    <TouchableOpacity
      style={[styles.notificationItem, !item.read && styles.unreadNotification]}
      onPress={() => handleNotificationPress(item)}
    >
      <View style={styles.notificationIconContainer}>
        {getNotificationIcon(item.type)}
        {!item.read && <View style={styles.unreadDot} />}
      </View>

      <View style={styles.notificationContent}>
        <View style={styles.notificationHeader}>
          <Text style={styles.notificationTitle} numberOfLines={1}>
            {item.title}
          </Text>
          <Text style={styles.notificationTime}>
            {formatTimestamp(item.timestamp)}
          </Text>
        </View>

        <Text style={styles.notificationMessage} numberOfLines={2}>
          {item.message}
        </Text>

        <View style={styles.notificationFooter}>
          <View style={styles.projectContainer}>
            <Ionicons name="folder-outline" size={14} color="#64748B" />
            <Text style={styles.projectName} numberOfLines={1}>
              {item.project.title}
            </Text>
          </View>

          <View
            style={[
              styles.priorityBadge,
              { backgroundColor: getPriorityColor(item.priority) },
            ]}
          >
            <Text style={styles.priorityText}>{item.priority}</Text>
          </View>
        </View>

        {item.actionRequired && (
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => handleActionPress(item)}
          >
            <Text style={styles.actionButtonText}>{item.actionText}</Text>
          </TouchableOpacity>
        )}
      </View>
    </TouchableOpacity>
  );

  // Render empty state
  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <MaterialCommunityIcons
        name="bell-off-outline"
        size={60}
        color="#CBD5E1"
      />
      <Text style={styles.emptyTitle}>No Notifications</Text>
      <Text style={styles.emptyText}>
        {filterType !== 'All'
          ? `You don't have any ${filterType.toLowerCase()} notifications at the moment.`
          : "You're all caught up! No new notifications."}
      </Text>
    </View>
  );

  // Render list header
  const renderListHeader = () => {
    const filteredNotifications = getFilteredNotifications();
    const filteredUnreadCount = filteredNotifications.filter(n => !n.read).length;

    return (
      <View style={styles.listHeader}>
        <View style={styles.listHeaderLeft}>
          <Text style={styles.listHeaderTitle}>
            {filterType === 'All' ? 'All Notifications' : filterType}
          </Text>
          {filteredUnreadCount > 0 && (
            <View style={styles.unreadCountBadge}>
              <Text style={styles.unreadCountText}>{filteredUnreadCount}</Text>
            </View>
          )}
        </View>

        {filteredNotifications.length > 0 && filteredUnreadCount > 0 && (
          <TouchableOpacity
            style={styles.markAllReadButton}
            onPress={markAllAsRead}
          >
            <Text style={styles.markAllReadText}>Mark all as read</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>

        <View style={styles.headerCenter}>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
          ) : (
            <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
          )}
          <Text style={styles.headerTitle}>Notifications</Text>
        </View>

        <View style={styles.headerRight}>
          {unreadCount > 0 && (
            <Animated.View
              style={[
                styles.headerBadge,
                { transform: [{ scale: pulseAnim }] },
              ]}
            >
              <Text style={styles.headerBadgeText}>{unreadCount}</Text>
            </Animated.View>
          )}
        </View>
      </View>

      {/* Filter Tabs */}
      <View style={styles.filterContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filtersScrollContent}
        >
          {filterOptions.map(option => (
            <TouchableOpacity
              key={option}
              style={[
                styles.filterTab,
                filterType === option && styles.filterTabActive,
              ]}
              onPress={() => setFilterType(option)}
            >
              <Text
                style={[
                  styles.filterTabText,
                  filterType === option && styles.filterTabTextActive,
                ]}
              >
                {option}
              </Text>

              {option !== 'All' &&
                notifications.filter(n => n.type === option && !n.read).length >
                  0 && (
                  <View style={styles.filterBadge}>
                    <Text style={styles.filterBadgeText}>
                      {notifications.filter(n => n.type === option && !n.read).length}
                    </Text>
                  </View>
                )}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Notifications List */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading notifications...</Text>
        </View>
      ) : (
        <FlatList
          data={getFilteredNotifications()}
          renderItem={renderNotificationItem}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.notificationsList}
          ListEmptyComponent={renderEmptyState}
          ListHeaderComponent={renderListHeader}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={['#0e4e96']}
              tintColor="#0e4e96"
            />
          }
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  header: {
    backgroundColor: '#0e4e96',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 5,
  },
  headerCenter: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerLogo: {
    width: 30,
    height: 30,
    borderRadius: 15,
    resizeMode: 'cover',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  headerRight: {
    width: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerBadge: {
    backgroundColor: '#EF4444',
    borderRadius: 12,
    minWidth: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  headerBadgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  filterContainer: {
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  filtersScrollContent: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  filterTab: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    marginRight: 8,
    backgroundColor: '#f1f5f9',
    flexDirection: 'row',
    alignItems: 'center',
  },
  filterTabActive: {
    backgroundColor: '#e0f2fe',
  },
  filterTabText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  filterTabTextActive: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  filterBadge: {
    backgroundColor: '#EF4444',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
    marginLeft: 6,
  },
  filterBadgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#64748B',
  },
  notificationsList: {
    backgroundColor: '#f8fafc',
    flexGrow: 1,
  },
  listHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  listHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  listHeaderTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
  },
  unreadCountBadge: {
    backgroundColor: '#0e4e96',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
    marginLeft: 8,
  },
  unreadCountText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  markAllReadButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  markAllReadText: {
    fontSize: 14,
    color: '#0e4e96',
    fontWeight: '500',
  },
  notificationItem: {
    backgroundColor: '#fff',
    borderRadius: 12,
    marginHorizontal: 16,
    marginTop: 12,
    padding: 16,
    flexDirection: 'row',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 5,
    elevation: 1,
  },
  unreadNotification: {
    backgroundColor: '#f0f9ff',
    borderLeftWidth: 4,
    borderLeftColor: '#0e4e96',
  },
  notificationIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f1f5f9',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  unreadDot: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#EF4444',
    borderWidth: 1,
    borderColor: '#fff',
  },
  notificationContent: {
    flex: 1,
  },
  notificationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  notificationTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    flex: 1,
    marginRight: 8,
  },
  notificationTime: {
    fontSize: 12,
    color: '#64748B',
  },
  notificationMessage: {
    fontSize: 14,
    color: '#334155',
    marginBottom: 8,
    lineHeight: 20,
  },
  notificationFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
  },
  projectContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 8,
  },
  projectName: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
  },
  priorityBadge: {
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: '500',
    color: '#fff',
  },
  actionButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignSelf: 'flex-start',
    marginTop: 8,
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    backgroundColor: '#f8fafc',
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
  },
});